import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import type { Group, ThemeClasses } from "../types";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import {
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../../utils/getDate";
interface GroupGridCardProps {
  group: Group;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (group: Group) => void;
  onDelete: (group: Group) => void;
  onViewDetails: (group: Group) => void;
}

export function GroupGridCard({
  group,
  themeClasses,
  onEdit,
  onDelete,
  onViewDetails,
}: GroupGridCardProps) {
  const { t } = useTranslation("adminSections");
  const { t: tLite } = useTranslation("lite");
  const disableCRUD = useDisableCRUD();
  const { isMonitor, isReadOnly } = useIsMonitor();

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tLite("time.noData"),
      rightNow: tLite("time.rightNow"),
      min: tLite("time.min"),
      hour: tLite("time.hour"),
      hours: tLite("time.hours"),
      day: tLite("time.day"),
      days: tLite("time.days"),
      month: tLite("time.month"),
      months: tLite("time.months"),
      and: tLite("time.and"),
    }),
    [tLite],
  );
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4 lg:p-5 hover:shadow-lg transition-all relative`}
    >
      {/* Header */}
      <div className="mb-3 sm:mb-4 pr-6">
        <h3
          className={`font-semibold ${themeClasses.text} text-base sm:text-lg truncate`}
        >
          {group.name}
        </h3>
      </div>

      {/* Details */}
      <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("groups.fields.description")}
          </span>
          <span
            className={`text-xs sm:text-sm font-medium ${themeClasses.text} truncate max-w-[150px]`}
          >
            {group.description || t("groups.fields.noDescription")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("groups.fields.status")}
          </span>
          <span
            className={`text-xs sm:text-sm font-medium ${themeClasses.text} truncate max-w-[150px]`}
          >
            {group?.status?.name
              ? getStatusHumanLabel(group.status.name, t)
              : t("groups.fields.noStatus")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("groups.fields.devices")}
          </span>
          <span
            className={`text-xs sm:text-sm font-medium ${themeClasses.text}`}
          >
            {group.devicesCount ?? group.devices_ids?.length ?? 0}{" "}
            {(group.devicesCount ?? group.devices_ids?.length ?? 0) !== 1
              ? t("groups.fields.devicesCountPlural")
              : t("groups.fields.devicesCount")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("groups.fields.updatedAt")}
          </span>
          <span
            className={`text-xs sm:text-sm font-medium ${themeClasses.text}`}
          >
            {getTimeAgo(group.updatedAt?._seconds ?? 0, timeTranslations)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2">
        <div className="w-full flex flex-col gap-1">
          {/* Ver detalles */}
          <button
            onClick={() => onViewDetails(group)}
            className="w-full py-1.5 px-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1"
          >
            <i className="ri-eye-line text-base"></i>
            <span>{t("groups.actions.details")}</span>
          </button>

          {/* Editar y Eliminar */}
          {!isMonitor && !isReadOnly && (
            <div className="flex gap-1 2xl:flex-1">
              <CRUDTooltip className="flex-1">
                <button
                  onClick={() => onEdit(group)}
                  disabled={disableCRUD}
                  className={`w-full py-1.5 px-1 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center gap-0.5 cursor-pointer`}
                >
                  <i className="ri-pencil-line text-base"></i>
                  <span>{t("groups.actions.edit")}</span>
                </button>
              </CRUDTooltip>
              <CRUDTooltip className="flex-1">
                <button
                  onClick={() => onDelete(group)}
                  disabled={disableCRUD}
                  className="w-full py-1.5 px-1 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 border border-red-200 dark:border-red-500/30 rounded-md text-red-600 dark:text-red-400 text-sm font-medium transition-colors flex items-center justify-center gap-0.5 cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-base"></i>
                  <span>{t("groups.actions.delete")}</span>
                </button>
              </CRUDTooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
